import React, { useState, useMemo } from 'react';
import { Button, Dialog, useDialogStore } from '@ariakit/react';
import { 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown, 
  Plus, 
  X, 
  GripVertical,
  SortAsc,
  Trash2
} from 'lucide-react';
import type { Table, Column, SortingState } from '@tanstack/react-table';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface MultiSortProps<TData> {
  table: Table<TData>;
}

interface SortItem {
  id: string;
  desc: boolean;
}

interface SortableItemProps<TData> {
  item: SortItem;
  column: Column<TData>;
  onRemove: () => void;
  onToggleDirection: () => void;
  index: number;
}

function SortableItem<TData>({ 
  item, 
  column, 
  onRemove, 
  onToggleDirection,
  index 
}: SortableItemProps<TData>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const columnName = typeof column.columnDef.header === 'string'
    ? column.columnDef.header
    : column.id;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        flex items-center gap-3 p-3 rounded-lg border
        ${isDragging ? 'opacity-50 bg-gray-100 border-gray-300' : 'bg-white border-gray-200 hover:border-gray-300'}
      `}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-move"
      >
        <GripVertical className="w-4 h-4 text-gray-400" />
      </div>
      
      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-semibold">
        {index + 1}
      </div>
      
      <div className="flex-1">
        <span className="text-sm font-medium text-gray-700">
          {columnName}
        </span>
      </div>
      
      <button
        onClick={onToggleDirection}
        className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border border-gray-200 hover:bg-gray-50"
        data-testid={`sort-direction-${item.id}`}
      >
        {item.desc ? (
          <>
            <ArrowDown className="w-3 h-3" />
            <span>Desc</span>
          </>
        ) : (
          <>
            <ArrowUp className="w-3 h-3" />
            <span>Asc</span>
          </>
        )}
      </button>
      
      <button
        onClick={onRemove}
        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
        data-testid={`remove-sort-${item.id}`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function MultiSort<TData>({ table }: MultiSortProps<TData>) {
  const dialog = useDialogStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  
  const currentSorting = table.getState().sorting;
  const [tempSorting, setTempSorting] = useState<SortingState>(currentSorting);
  
  // Sync tempSorting with external changes to sorting
  React.useEffect(() => {
    setTempSorting(currentSorting);
  }, [currentSorting]);
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  const sortableColumns = table.getAllColumns().filter(
    column => column.getCanSort()
  );
  
  const availableColumns = useMemo(() => {
    const sortedIds = tempSorting.map(s => s.id);
    return sortableColumns.filter(col => !sortedIds.includes(col.id));
  }, [sortableColumns, tempSorting]);
  
  const filteredAvailableColumns = useMemo(() => {
    if (!searchQuery) return availableColumns;
    
    return availableColumns.filter(column => {
      const header = column.columnDef.header;
      const columnName = typeof header === 'string' ? header : column.id;
      return columnName.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [availableColumns, searchQuery]);
  
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };
  
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setTempSorting(items => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
    
    setActiveId(null);
  };
  
  const handleAddSort = (columnId: string) => {
    setTempSorting(prev => [...prev, { id: columnId, desc: false }]);
  };
  
  const handleRemoveSort = (columnId: string) => {
    setTempSorting(prev => prev.filter(item => item.id !== columnId));
  };
  
  const handleToggleDirection = (columnId: string) => {
    setTempSorting(prev =>
      prev.map(item =>
        item.id === columnId ? { ...item, desc: !item.desc } : item
      )
    );
  };
  
  const handleApply = () => {
    table.setSorting(tempSorting);
    dialog.hide();
  };
  
  const handleCancel = () => {
    setTempSorting(currentSorting);
    dialog.hide();
  };
  
  const handleClearAll = () => {
    setTempSorting([]);
  };
  
  const activeColumn = activeId 
    ? table.getColumn(activeId)
    : null;
  
  const activeColumnName = activeColumn
    ? (typeof activeColumn.columnDef.header === 'string'
        ? activeColumn.columnDef.header
        : activeColumn.id)
    : '';

  return (
    <>
      <Button
        onClick={dialog.show}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        data-testid="multi-sort-button"
      >
        <SortAsc className="w-4 h-4" />
        Multi-Sort ({tempSorting.length})
      </Button>

      <Dialog
        store={dialog}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        backdrop={
          <div className="fixed inset-0 bg-black/50" />
        }
        data-testid="multi-sort-dialog"
      >
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Configure Multi-Sort
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Add columns to sort by and drag to change priority
            </p>
          </div>

          <div className="flex-1 overflow-hidden flex">
            <div className="flex-1 p-6 overflow-y-auto border-r border-gray-200">
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Active Sorts ({tempSorting.length})
                </h3>
                
                {tempSorting.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">
                    No sorting applied. Add columns from the right panel.
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={tempSorting.map(s => s.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {tempSorting.map((item, index) => {
                          if (!table.getAllColumns().length) { return null; }

                          const column = table.getColumn(item.id);
                          if (!column) return null;
                          
                          return (
                            <div key={item.id} data-testid={`sort-item-${item.id}`}>
                              <SortableItem
                                item={item}
                                column={column}
                                index={index}
                                onRemove={() => handleRemoveSort(item.id)}
                                onToggleDirection={() => handleToggleDirection(item.id)}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </SortableContext>
                    
                    <DragOverlay>
                      {activeId && activeColumn ? (
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-white shadow-lg border border-gray-300">
                          <GripVertical className="w-4 h-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-700">
                            {activeColumnName}
                          </span>
                        </div>
                      ) : null}
                    </DragOverlay>
                  </DndContext>
                )}
              </div>
            </div>

            <div className="w-72 p-6 bg-gray-50">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Available Columns
              </h3>
              
              <input
                type="text"
                placeholder="Search columns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
              />
              
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {filteredAvailableColumns.length === 0 ? (
                  <div className="text-center py-4 text-gray-400 text-sm">
                    {searchQuery ? 'No columns found' : 'All columns are being sorted'}
                  </div>
                ) : (
                  filteredAvailableColumns.map(column => {
                    const columnName = typeof column.columnDef.header === 'string'
                      ? column.columnDef.header
                      : column.id;
                    
                    return (
                      <button
                        key={column.id}
                        onClick={() => handleAddSort(column.id)}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm bg-white border border-gray-200 rounded-md hover:bg-gray-50 hover:border-gray-300"
                        data-testid={`add-sort-${column.id}`}
                      >
                        <span className="text-gray-700">{columnName}</span>
                        <Plus className="w-4 h-4 text-gray-400" />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
            <Button
              onClick={handleClearAll}
              className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              disabled={tempSorting.length === 0}
              data-testid="multi-sort-clear-all"
            >
              <Trash2 className="w-4 h-4 mr-2 inline" />
              Clear All
            </Button>
            
            <div className="flex gap-3">
              <Button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                data-testid="multi-sort-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleApply}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                data-testid="multi-sort-apply"
              >
                Apply Sorting
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}
